import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Chip,
  useTheme,
  alpha,
  Stack,
  Divider,
  Tooltip,
  Alert,
  Button,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  Assessment as MetricsIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import useFinancialTableData from '../../hooks/useFinancialTableData';

/**
 * HierarchicalFinancialTable - Mimics spreadsheet template structure
 * Features:
 * - Color-coded financial categories
 * - Expandable brand > campaign > media type hierarchy
 * - Comprehensive financial breakdown
 * - Responsive design
 */
const HierarchicalFinancialTable = ({ 
  year,
  month,
  brandId,
  campaignId,
  mediaType,
  className = '',
  sx = {},
  autoRefresh = false,
  refreshInterval = 5 * 60 * 1000,
  // New snapshot mode props
  isSnapshotMode = false,
  snapshotDate = null,
  selectedSnapshotMonth = null,
  selectedSnapshotMonthStr = null,
  ...props 
}) => {
  const theme = useTheme();
  const [expandedBrands, setExpandedBrands] = useState(new Set());
  const [selectedBrandFilter, setSelectedBrandFilter] = useState('All');

  // Dynamic month calculation based on selected month (prefer explicit selection)
  const getCurrentMonthHeaders = () => {
    // Month names array for conversion
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    // Prefer explicit selected snapshot month if provided (even if isSnapshotMode hasn't toggled yet)
    if (selectedSnapshotMonthStr || (isSnapshotMode && selectedSnapshotMonth)) {
      // Prefer full YYYY-MM string if available, fallback to numeric month
      let currentMonthIndex;
      if (selectedSnapshotMonthStr) {
        try {
          const parts = String(selectedSnapshotMonthStr).split('-');
          currentMonthIndex = (parseInt(parts[1], 10) - 1) % 12;
        } catch (e) {
          currentMonthIndex = selectedSnapshotMonth ? (selectedSnapshotMonth - 1) % 12 : (new Date().getMonth());
        }
      } else {
        currentMonthIndex = (selectedSnapshotMonth - 1) % 12;
      }
      const lastMonthIndex = currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
      
      return {
        lastMonth: monthNames[lastMonthIndex], // Previous month (n-1 of selected)
        thisMonth: monthNames[currentMonthIndex], // Selected month
        isSnapshotReady: true
      };
    }
    
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11
    const currentDay = today.getDate();
    
    // If before 15th, use previous month's data for both columns
    // If after 15th, current month becomes "this month" and previous becomes "last month"
    let thisMonth, lastMonth;
    
    if (currentDay < 15) {
      // Before 15th: still working with previous month's data
      thisMonth = currentMonth === 0 ? 11 : currentMonth - 1; // Handle January edge case
      lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    } else {
      // After 15th: snapshot is ready, move to current month
      thisMonth = currentMonth;
      lastMonth = currentMonth === 0 ? 11 : currentMonth - 1; // Handle January edge case
    }
    
    return {
      lastMonth: monthNames[lastMonth],
      thisMonth: monthNames[thisMonth],
      isSnapshotReady: currentDay >= 15
    };
  };

  // Convert selectedSnapshotMonth to snapshotDate format
  const convertToSnapshotDate = (monthNumber) => {
    if (!isSnapshotMode) return null;
    // Prefer full YYYY-MM if provided
    if (selectedSnapshotMonthStr && /^\d{4}-\d{2}$/.test(selectedSnapshotMonthStr)) {
      return selectedSnapshotMonthStr;
    }
    if (!monthNumber) return null;
    const currentYear = new Date().getFullYear();
    const monthStr = monthNumber.toString().padStart(2, '0');
    return `${currentYear}-${monthStr}`;
  };

  const { lastMonth, thisMonth } = getCurrentMonthHeaders();
  
  // Helper function to check if media type belongs to ICON (social) group
  const isIconMediaType = (mediaType) => {
    if (!mediaType) return false;
    
    const mediaTypeLower = mediaType.toLowerCase();
    
    // Social media keywords for ICON classification
    const socialKeywords = ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'];
    return socialKeywords.some(keyword => mediaTypeLower.includes(keyword));
  };

  // Calculate previous month for snapshot data
  const getPreviousMonthSnapshotDate = () => {
    // Use selectedSnapshotMonthStr if available to handle year boundaries
    if (isSnapshotMode && selectedSnapshotMonthStr && /^\d{4}-\d{2}$/.test(selectedSnapshotMonthStr)) {
      const [y, m] = selectedSnapshotMonthStr.split('-').map(v => parseInt(v, 10));
      const prev = new Date(y, m - 2, 1); // JS month is 0-based; m-2 = previous month
      const prevY = prev.getFullYear();
      const prevM = String(prev.getMonth() + 1).padStart(2, '0');
      return `${prevY}-${prevM}`;
    }
    const currentYear = new Date().getFullYear();
    let targetMonth;
    
    if (isSnapshotMode && selectedSnapshotMonth) {
      targetMonth = selectedSnapshotMonth === 1 ? 12 : selectedSnapshotMonth - 1;
    } else {
      // In current mode: get n-1 of displayed month (not current month)
      // If current is Aug < 15th, display is Jul, so snapshot should be Jun
      const today = new Date();
      const currentMonth = today.getMonth() + 1; // 1-based
      const currentDay = today.getDate();
      
      let displayedMonth;
      if (currentDay < 15) {
        // Display previous month, so snapshot is 2 months back
        displayedMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        targetMonth = displayedMonth === 1 ? 12 : displayedMonth - 1;
      } else {
        // Display current month, so snapshot is 1 month back  
        targetMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      }
    }
    
    const monthStr = targetMonth.toString().padStart(2, '0');
    return `${currentYear}-${monthStr}`;
  };

  // Debug current date info for month calculation
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  console.log('📅 Current Date Debug:', {
    today: today.toISOString().split('T')[0],
    currentMonth,
    currentDay,
    isBeforeDay15: currentDay < 15,
    calculatedPreviousMonth: getPreviousMonthSnapshotDate()
  });

  // Debug log for month headers and data fetching strategy
  console.log('🗓️ Data Fetching Strategy Debug:', {
    isSnapshotMode,
    selectedSnapshotMonth,
    currentDataRequest: {
      isSnapshot: isSnapshotMode,
      snapshotDate: isSnapshotMode ? convertToSnapshotDate(selectedSnapshotMonth) : null,
      description: isSnapshotMode ? `Snapshot data for month ${selectedSnapshotMonth}` : 'Live current data',
      expectedEndpoint: isSnapshotMode ? 
        `new-snapshots/campaigns/?snapshot_date=${convertToSnapshotDate(selectedSnapshotMonth)}` : 
        'campaigns-data/ (live data)'
    },
    snapshotDataRequest: {
      isSnapshot: true,  
      snapshotDate: getPreviousMonthSnapshotDate(),
      description: `Snapshot data for previous month (n-1)`,
      expectedEndpoint: `new-snapshots/campaigns/?snapshot_date=${getPreviousMonthSnapshotDate()}`
    },
    monthHeaders: {
      lastMonth,
      thisMonth
    }
  });

  // Fetch data based on mode - optimize to prevent unnecessary calls
  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
    refreshData: refreshCurrentData
  } = useFinancialTableData({
    year,
    month,
    brandId,
    campaignId,
    mediaType,
    autoRefresh: false, // Disable auto-refresh to prevent loops
    refreshInterval,
    useCache: true,
    isSnapshotMode: isSnapshotMode,
    snapshotDate: isSnapshotMode ? convertToSnapshotDate(selectedSnapshotMonth) : null
  });

  // Only fetch snapshot data if we have current data and need comparison
  // Use a more efficient condition
  const shouldFetchSnapshot = useMemo(() => {
    return currentData && currentData.length > 0 && !currentLoading;
  }, [currentData, currentLoading]);
  
  const {
    data: snapshotData,
    loading: snapshotLoading,
    error: snapshotError,
    refreshData: refreshSnapshotData
  } = useFinancialTableData({
    year,
    brandId,
    campaignId,
    mediaType,
    autoRefresh: false,
    refreshInterval,
    useCache: true,
    isSnapshotMode: true,
    snapshotDate: getPreviousMonthSnapshotDate(),
    enabled: shouldFetchSnapshot // Only fetch when needed
  });

  // Combine loading states and errors
  const loading = currentLoading || snapshotLoading;
  const error = currentError || snapshotError;
  const isEmpty = (!currentData || currentData.length === 0) && (!snapshotData || snapshotData.length === 0);
  const hasData = (currentData && currentData.length > 0) || (snapshotData && snapshotData.length > 0);
  
  // Combined refresh function
  const refreshData = () => {
    refreshCurrentData();
    refreshSnapshotData();
  };
  
  // Mock other required properties for compatibility
  const meta = { hasSnapshot: snapshotData && snapshotData.length > 0 };
  const lastFetched = new Date();
  const clearError = () => {};
  const getDataFreshness = () => 'just now';
  
  // Remove debug logging to prevent infinite loop
  
  // Remove debug logging to prevent infinite loop

  // Unified color scheme - each column group has consistent colors
  const colorScheme = {
    // MONTH DATA section - Green family
    given: {
      bg: '#2E7D32', // Dark Green - Current paid media
      border: '#1B5E20',
      text: '#FFFFFF',
      exact: '#2E7D32'
    },
    givenSocial: {
      bg: '#4CAF50', // Medium Green - Current social media
      border: '#2E7D32', 
      text: '#FFFFFF',
      exact: '#4CAF50'
    },
    givenSnapshot: {
      bg: '#66BB6A', // Light Green - Snapshot paid media
      border: '#4CAF50',
      text: '#FFFFFF',
      exact: '#66BB6A'
    },
    givenSocialSnapshot: {
      bg: '#81C784', // Very Light Green - Snapshot social
      border: '#66BB6A',
      text: '#FFFFFF',
      exact: '#81C784'
    },
    
    // COMPARISON section - Orange family (unified)
    summary: {
      bg: '#FF8F00', // Medium Orange - Total summary
      border: '#F57F17',
      text: '#FFFFFF',
      exact: '#FF8F00'
    },
    highlight: {
      bg: '#FFA000', // Light Orange - Gap analysis (same family as summary)
      border: '#FF8F00',
      text: '#FFFFFF',
      exact: '#FFA000'
    },
    
    // ICON section - Blue family (unified)
    iconMain: {
      bg: '#1976D2', // Main Blue - ICON primary
      border: '#0D47A1',
      text: '#FFFFFF',
      exact: '#1976D2'
    },
    iconSecondary: {
      bg: '#1E88E5', // Light Blue - ICON secondary
      border: '#1976D2',
      text: '#FFFFFF',
      exact: '#1E88E5'
    },
    iconTertiary: {
      bg: '#2196F3', // Lighter Blue - ICON tertiary
      border: '#1E88E5',
      text: '#FFFFFF',
      exact: '#2196F3'
    },
    iconLight: {
      bg: '#42A5F5', // Lightest Blue - ICON light
      border: '#2196F3',
      text: '#FFFFFF',
      exact: '#42A5F5'
    },
    
    // RS COST section - Purple family (unified)
    rsPrimary: {
      bg: '#7B1FA2', // Dark Purple - RS primary
      border: '#4A148C',
      text: '#FFFFFF',
      exact: '#7B1FA2'
    },
    rsSecondary: {
      bg: '#8E24AA', // Medium Purple - RS secondary
      border: '#7B1FA2',
      text: '#FFFFFF',
      exact: '#8E24AA'
    },
    rsTertiary: {
      bg: '#9C27B0', // Light Purple - RS tertiary
      border: '#8E24AA',
      text: '#FFFFFF',
      exact: '#9C27B0'
    },
    rsLight: {
      bg: '#AB47BC', // Lightest Purple - RS light
      border: '#9C27B0',
      text: '#FFFFFF',
      exact: '#AB47BC'
    },
    
    // OVERVIEW section - Grey family (unified)
    overviewPrimary: {
      bg: '#455A64', // Dark Grey - Overview primary
      border: '#263238',
      text: '#FFFFFF',
      exact: '#455A64'
    },
    overviewSecondary: {
      bg: '#546E7A', // Medium Grey - Overview secondary
      border: '#455A64',
      text: '#FFFFFF',
      exact: '#546E7A'
    },
    overviewTertiary: {
      bg: '#607D8B', // Light Grey - Overview tertiary
      border: '#546E7A',
      text: '#FFFFFF',
      exact: '#607D8B'
    },
    overviewLight: {
      bg: '#78909C', // Lightest Grey - Overview light
      border: '#607D8B',
      text: '#FFFFFF',
      exact: '#78909C'
    },
    
    // Legacy/Fallback colors for backward compatibility
    performance: {
      bg: '#1976D2', // Blue - Performance/ICON data (alias for iconMain)
      border: '#0D47A1',
      text: '#FFFFFF',
      exact: '#1976D2'
    },
    planning: {
      bg: '#7B1FA2', // Purple - Planning data (alias for rsPrimary)
      border: '#4A148C',
      text: '#FFFFFF',
      exact: '#7B1FA2'
    },
    actual: {
      bg: '#AB47BC', // Light Purple - Actual cost data (alias for rsLight)
      border: '#9C27B0', 
      text: '#FFFFFF',
      exact: '#AB47BC'
    },
    breakdown: {
      bg: '#455A64', // Dark Grey - Breakdown/Overview (alias for overviewPrimary)
      border: '#263238',
      text: '#FFFFFF',
      exact: '#455A64'
    },
    savings: {
      bg: '#607D8B', // Light Grey - Savings data (alias for overviewTertiary)
      border: '#546E7A',
      text: '#FFFFFF',
      exact: '#607D8B'
    },
    savingsPercent: {
      bg: '#78909C', // Lightest Grey - Savings percentage (alias for overviewLight)
      border: '#607D8B',
      text: '#FFFFFF',
      exact: '#78909C'
    },
    
    // Headers - modern neutrals
    neutral: {
      bg: '#37474F', // Dark Blue Grey - Main headers
      border: '#263238',
      text: '#FFFFFF',
      exact: '#37474F'
    },
    sectionHeader: {
      bg: '#546E7A', // Medium Blue Grey - Section headers
      border: '#37474F',
      text: '#FFFFFF',
      exact: '#546E7A'
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '₫0';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0%';
    return `${Number(value).toFixed(1)}%`;
  };

  const calculateSavings = (planning, actual) => {
    // DEPRECATED: Use provisional_saving field from database instead
    // This function is kept for backward compatibility with percentage calculations
    if (!planning || !actual) return 0;
    return planning - actual;
  };

  const calculateSavingsPercentage = (planning, actual) => {
    if (!planning) return 0;
    const savings = calculateSavings(planning, actual);
    return (savings / planning) * 100;
  };

  const calculateGap = (currentValue, snapshotValue) => {
    // Ensure we always get numbers, default to 0 for null/undefined
    const current = parseFloat(currentValue) || 0;
    const snapshot = parseFloat(snapshotValue) || 0;
    const gap = current - snapshot;
    
    // Remove debug logging to prevent infinite loop
    return gap;
  };

  const toggleBrandExpansion = (brandId) => {
    const newExpanded = new Set(expandedBrands);
    if (newExpanded.has(brandId)) {
      newExpanded.delete(brandId);
    } else {
      newExpanded.add(brandId);
    }
    setExpandedBrands(newExpanded);
  };

  // Extract unique brands for filter tabs
  const availableBrands = useMemo(() => {
    const brands = new Set();
    currentData.forEach(item => {
      if (item.brand_name) {
        // Handle case where brand_name might be an object
        const brandName = typeof item.brand_name === 'object' 
          ? item.brand_name.brand_name || item.brand_name.name || 'Unknown Brand'
          : item.brand_name;
        brands.add(brandName);
      }
    });
    return Array.from(brands).sort();
  }, [currentData]);

  // Handle brand filter change
  const handleBrandFilterChange = (event, newValue) => {
    setSelectedBrandFilter(newValue);
    // Clear expanded brands when filter changes
    setExpandedBrands(new Set());
  };


  // Helper function to process dataset (current or snapshot) - optimized
  const processDataSet = useCallback((dataset, isSnapshot = false) => {
    if (!dataset || dataset.length === 0) {
      return new Map();
    }
    
    const brandMap = new Map();
    
    // Process items in batches to prevent blocking
    const batchSize = 100;
    const processBatch = (items) => {
      items.forEach(item => {
      
      // Handle case where brand_name might be an object
      const brandKey = typeof item.brand_name === 'object' 
        ? item.brand_name.brand_name || item.brand_name.name || 'Unknown Brand'
        : item.brand_name || 'Unknown Brand';
      // ICON vs RS classification
      // ICON = social media (Facebook, Instagram, YouTube, TikTok, etc.)
      // RS (Red Star) = all other paid media types (TV, Radio, Digital non-social, OOH, etc.)
      const isIconSection = isIconMediaType(item.media_type) || item.section === 'ICON';
      const isSocialMedia = isIconSection;
      
      if (!brandMap.has(brandKey)) {
        brandMap.set(brandKey, {
          brand_name: brandKey,
          campaigns: new Map(),
          totals: {
            // Separate totals for ICON (social) and RS COST (non-social)
            // Each section has current and snapshot data for comparison
            icon: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            },
            rsCost: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            },
            combined: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            }
          }
        });
      }
      
      const brand = brandMap.get(brandKey);
      // Handle case where campaign_name might be an object
      const campaignKey = typeof item.campaign_name === 'object' 
        ? item.campaign_name.campaign_name || item.campaign_name.name || 'Unknown Campaign'
        : item.campaign_name || 'Unknown Campaign';
      
      if (!brand.campaigns.has(campaignKey)) {
        brand.campaigns.set(campaignKey, {
          campaign_name: campaignKey,
          media_types: [],
          iconData: [], // Social media data
          rsCostData: [], // Non-social media data
          totals: {
            icon: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            },
            rsCost: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            },
            combined: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
            }
          }
        });
      }
      
      const campaign = brand.campaigns.get(campaignKey);
      
      // Add to appropriate section based on ICON vs RS classification
      if (isIconSection) {
        campaign.iconData.push(item);
      } else {
        campaign.rsCostData.push(item);
      }
      
      // Add to media types for backward compatibility
      campaign.media_types.push(item);
      
      // Calculate values based on section classification
      let given, givenSocial;
      
      if (isIconSection) {
        // ICON (social): amount goes to social column
        given = 0;
        givenSocial = parseFloat(item.given_social || item.given || item.planning_gross_cost || 0);
      } else {
        // RS (Red Star): amount goes to paid media column  
        given = parseFloat(item.given_paid_media || item.given || item.planning_gross_cost || 0);
        givenSocial = 0;
      }
      
      // Remove debug logging to prevent infinite loop
      
      const planning = parseFloat(item.planning_gross_cost || 0);
      const actual = parseFloat(item.actual_gross_cost || 0);
      const savings = parseFloat(item.provisional_saving || 0);
      
      const sectionKey = isIconSection ? 'icon' : 'rsCost';
      const dataType = isSnapshot ? 'snapshot' : 'current';
      
      // Remove debug logging to prevent infinite loop
      
      // Update campaign totals for specific section
      campaign.totals[sectionKey][dataType].given += given;
      campaign.totals[sectionKey][dataType].given_social += givenSocial;
      campaign.totals[sectionKey][dataType].planning += planning;
      campaign.totals[sectionKey][dataType].actual += actual;
      campaign.totals[sectionKey][dataType].savings += savings;
      
      // Update campaign combined totals
      campaign.totals.combined[dataType].given += given;
      campaign.totals.combined[dataType].given_social += givenSocial;
      campaign.totals.combined[dataType].planning += planning;
      campaign.totals.combined[dataType].actual += actual;
      campaign.totals.combined[dataType].savings += savings;
      
      // Update brand totals for specific section
      brand.totals[sectionKey][dataType].given += given;
      brand.totals[sectionKey][dataType].given_social += givenSocial;
      brand.totals[sectionKey][dataType].planning += planning;
      brand.totals[sectionKey][dataType].actual += actual;
      brand.totals[sectionKey][dataType].savings += savings;
      
      // Update brand combined totals
      brand.totals.combined[dataType].given += given;
      brand.totals.combined[dataType].given_social += givenSocial;
      brand.totals.combined[dataType].planning += planning;
      brand.totals.combined[dataType].actual += actual;
      brand.totals.combined[dataType].savings += savings;
      
      // Remove debug logging to prevent infinite loop
      });
    };
    
    // Process in batches to prevent UI blocking
    for (let i = 0; i < dataset.length; i += batchSize) {
      const batch = dataset.slice(i, i + batchSize);
      processBatch(batch);
    }
    
    return brandMap;
  }, []); // Empty dependency array for stability

  // Process data into hierarchical structure with ICON/RS separation - optimized with debouncing
  const processedData = useMemo(() => {
    const startTime = performance.now();
    
    // Early return if no data
    if ((!currentData || currentData.length === 0) && (!snapshotData || snapshotData.length === 0)) {
      return [];
    }
    
    // Process current data
    const currentBrandMap = processDataSet(currentData || [], false);
    // Process snapshot data - process as 'current' then we'll copy to 'snapshot' in merge
    const snapshotBrandMap = processDataSet(snapshotData || [], false);
    
    // Merge current and snapshot data
    const mergedBrandMap = new Map();
    
    // Add all brands from current data
    currentBrandMap.forEach((brand, brandKey) => {
      mergedBrandMap.set(brandKey, brand);
    });
    
    // Merge snapshot data into existing brands or create new ones
    snapshotBrandMap.forEach((snapshotBrand, brandKey) => {
      if (mergedBrandMap.has(brandKey)) {
        const currentBrand = mergedBrandMap.get(brandKey);
        
        // Merge snapshot totals into current brand
        ['icon', 'rsCost', 'combined'].forEach(section => {
          if (snapshotBrand.totals[section] && snapshotBrand.totals[section].current) {
            // Copy snapshot data from snapshotBrand's 'current' key to currentBrand's 'snapshot' key
            const snapshotSectionData = snapshotBrand.totals[section].current;
            
            currentBrand.totals[section].snapshot = {
              given: snapshotSectionData.given || 0,
              given_social: snapshotSectionData.given_social || 0,
              planning: snapshotSectionData.planning || 0,
              actual: snapshotSectionData.actual || 0,
              savings: snapshotSectionData.savings || 0
            };
            
            // Remove debug logging to prevent infinite loop
          } else {
            // Remove debug logging to prevent infinite loop
          }
        });
        
        // Merge campaign snapshot data
        snapshotBrand.campaigns.forEach((snapshotCampaign, campaignKey) => {
          if (currentBrand.campaigns.has(campaignKey)) {
            const currentCampaign = currentBrand.campaigns.get(campaignKey);
            // Merge snapshot totals into current campaign
            ['icon', 'rsCost', 'combined'].forEach(section => {
              if (snapshotCampaign.totals[section] && snapshotCampaign.totals[section].current) {
                // Copy snapshot data from snapshotCampaign's 'current' key to currentCampaign's 'snapshot' key
                const snapshotSectionData = snapshotCampaign.totals[section].current;
                
                currentCampaign.totals[section].snapshot = {
                  given: snapshotSectionData.given || 0,
                  given_social: snapshotSectionData.given_social || 0,
                  planning: snapshotSectionData.planning || 0,
                  actual: snapshotSectionData.actual || 0,
                  savings: snapshotSectionData.savings || 0
                };
                
                // Remove debug logging to prevent infinite loop
              }
            });
          } else {
            // Add snapshot-only campaign - move data from 'current' to 'snapshot'
            const snapshotOnlyCampaign = {
              ...snapshotCampaign,
              totals: {
                icon: {
                  current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
                  snapshot: snapshotCampaign.totals.icon.current
                },
                rsCost: {
                  current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
                  snapshot: snapshotCampaign.totals.rsCost.current
                },
                combined: {
                  current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
                  snapshot: snapshotCampaign.totals.combined.current
                }
              }
            };
            currentBrand.campaigns.set(campaignKey, snapshotOnlyCampaign);
          }
        });
      } else {
        // Add snapshot-only brand - move data from 'current' to 'snapshot' 
        const snapshotOnlyBrand = {
          ...snapshotBrand,
          totals: {
            icon: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: snapshotBrand.totals.icon.current
            },
            rsCost: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: snapshotBrand.totals.rsCost.current
            },
            combined: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: snapshotBrand.totals.combined.current
            }
          }
        };
        
        // Also move campaign data from 'current' to 'snapshot'
        snapshotOnlyBrand.campaigns = Array.from(snapshotBrand.campaigns.values()).map(campaign => ({
          ...campaign,
          totals: {
            icon: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: campaign.totals.icon.current
            },
            rsCost: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: campaign.totals.rsCost.current
            },
            combined: {
              current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
              snapshot: campaign.totals.combined.current
            }
          }
        }));
        
        mergedBrandMap.set(brandKey, snapshotOnlyBrand);
      }
    });
    
    let result = Array.from(mergedBrandMap.values()).map(brand => ({
      ...brand,
      campaigns: Array.from(brand.campaigns.values())
    }));

    // Apply brand filter
    if (selectedBrandFilter !== 'All') {
      result = result.filter(brand => {
        const brandName = typeof brand.brand_name === 'object' 
          ? brand.brand_name.brand_name || brand.brand_name.name || 'Unknown Brand'
          : brand.brand_name;
        return brandName === selectedBrandFilter;
      });
    }
    
    // Performance monitoring
    const endTime = performance.now();
    const processingTime = endTime - startTime;
    
    if (processingTime > 100) { // Log if processing takes more than 100ms
      console.warn(`⚠️ Data processing took ${processingTime.toFixed(2)}ms`, {
        currentDataLength: currentData?.length || 0,
        snapshotDataLength: snapshotData?.length || 0,
        resultLength: result.length
      });
    }
    
    return result;
  }, [currentData, snapshotData, selectedBrandFilter]);

  // Summary calculations with current/snapshot structure
  const grandTotals = useMemo(() => {
    return processedData.reduce((acc, brand) => ({
      icon: {
        current: {
          given: acc.icon.current.given + brand.totals.icon.current.given,
          given_social: acc.icon.current.given_social + brand.totals.icon.current.given_social,
          planning: acc.icon.current.planning + brand.totals.icon.current.planning,
          actual: acc.icon.current.actual + brand.totals.icon.current.actual,
          savings: acc.icon.current.savings + brand.totals.icon.current.savings
        },
        snapshot: {
          given: acc.icon.snapshot.given + brand.totals.icon.snapshot.given,
          given_social: acc.icon.snapshot.given_social + brand.totals.icon.snapshot.given_social,
          planning: acc.icon.snapshot.planning + brand.totals.icon.snapshot.planning,
          actual: acc.icon.snapshot.actual + brand.totals.icon.snapshot.actual,
          savings: acc.icon.snapshot.savings + brand.totals.icon.snapshot.savings
        }
      },
      rsCost: {
        current: {
          given: acc.rsCost.current.given + brand.totals.rsCost.current.given,
          given_social: acc.rsCost.current.given_social + brand.totals.rsCost.current.given_social,
          planning: acc.rsCost.current.planning + brand.totals.rsCost.current.planning,
          actual: acc.rsCost.current.actual + brand.totals.rsCost.current.actual,
          savings: acc.rsCost.current.savings + brand.totals.rsCost.current.savings
        },
        snapshot: {
          given: acc.rsCost.snapshot.given + brand.totals.rsCost.snapshot.given,
          given_social: acc.rsCost.snapshot.given_social + brand.totals.rsCost.snapshot.given_social,
          planning: acc.rsCost.snapshot.planning + brand.totals.rsCost.snapshot.planning,
          actual: acc.rsCost.snapshot.actual + brand.totals.rsCost.snapshot.actual,
          savings: acc.rsCost.snapshot.savings + brand.totals.rsCost.snapshot.savings
        }
      },
      combined: {
        current: {
          given: acc.combined.current.given + brand.totals.combined.current.given,
          given_social: acc.combined.current.given_social + brand.totals.combined.current.given_social,
          planning: acc.combined.current.planning + brand.totals.combined.current.planning,
          actual: acc.combined.current.actual + brand.totals.combined.current.actual,
          savings: acc.combined.current.savings + brand.totals.combined.current.savings
        },
        snapshot: {
          given: acc.combined.snapshot.given + brand.totals.combined.snapshot.given,
          given_social: acc.combined.snapshot.given_social + brand.totals.combined.snapshot.given_social,
          planning: acc.combined.snapshot.planning + brand.totals.combined.snapshot.planning,
          actual: acc.combined.snapshot.actual + brand.totals.combined.snapshot.actual,
          savings: acc.combined.snapshot.savings + brand.totals.combined.snapshot.savings
        }
      }
    }), { 
      icon: {
        current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
        snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
      },
      rsCost: {
        current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
        snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
      },
      combined: {
        current: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 },
        snapshot: { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 }
      }
    });
  }, [processedData]);

  // Helper function to get totals for display (current month)
  const getTotals = (totalsObj, section = 'current') => {
    const result = totalsObj?.[section] || { given: 0, given_social: 0, planning: 0, actual: 0, savings: 0 };
    
    // Remove debug logging to prevent infinite loop
    return result;
  };




  const ColorCodedCell = ({ value, type, align = "right", sx = {}, autoScale = true }) => {
    const displayValue = () => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'string' && value.includes('%')) return value;
      if (typeof value === 'number') return formatCurrency(value);
      return value;
    };

    // Fallback to neutral color if type is not found
    const colorConfig = colorScheme[type] || colorScheme.neutral;
    
    // Debug log for undefined color types
    if (!colorScheme[type]) {
      console.warn(`⚠️ Color type "${type}" not found in colorScheme. Using neutral fallback.`, {
        availableTypes: Object.keys(colorScheme),
        requestedType: type
      });
    }

    // Standardize border width for consistent appearance
    const getBorderWidth = (type) => {
      // Use uniform 1px border for all cells to avoid visual inconsistency
      return '1px';
    };

    // Calculate dynamic width based on content length for auto scaling
    const getAutoScaleWidth = (value, type) => {
      if (!autoScale) return {};
      
      const displayText = displayValue();
      const textLength = displayText.toString().length;
      
      // Base minimum widths for different column types
      const minWidths = {
        'given': 130,
        'givenSocial': 120,
        'givenSnapshot': 130,
        'givenSocialSnapshot': 120,
        'summary': 150,
        'highlight': 140,
        'iconMain': 120,
        'iconSecondary': 120,
        'iconTertiary': 130,
        'iconLight': 130,
        'rsPrimary': 120,
        'rsSecondary': 120,
        'rsTertiary': 130,
        'rsLight': 130,
        'overviewPrimary': 140,
        'overviewSecondary': 120,
        'overviewTertiary': 140,
        'overviewLight': 120
      };
      
      const minWidth = minWidths[type] || 100;
      
      // Calculate width based on content length (roughly 8-10px per character)
      const contentWidth = Math.max(minWidth, textLength * 9 + 20); // +20 for padding
      
      return {
        minWidth: minWidth,
        width: contentWidth,
        maxWidth: contentWidth + 20 // Allow some flexibility
      };
    };

    const autoScaleStyles = getAutoScaleWidth(value, type);

    return (
      <TableCell
        align={align}
        sx={{
          bgcolor: colorConfig?.bg || '#37474F',
          border: `${getBorderWidth(type)} solid ${colorConfig?.border || '#263238'}`,
          color: colorConfig?.text || '#FFFFFF',
          fontWeight: type === 'given' || type === 'planning' || type === 'actual' || type === 'neutral' ? 700 : 500,
          borderCollapse: 'separate',
          whiteSpace: 'nowrap', // Prevent number wrapping
          overflow: 'visible', // Allow content to show fully
          ...autoScaleStyles,
          ...sx
        }}
      >
        {displayValue()}
      </TableCell>
    );
  };

  // Loading state with skeleton
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 4,
        minHeight: 300
      }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {isSnapshotMode ? 'Loading snapshot data...' : 'Loading current data...'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSnapshotMode ? 
            `Fetching snapshot data for ${selectedSnapshotMonth ? `month ${selectedSnapshotMonth}` : 'selected month'}...` :
            'Please wait while we fetch the latest information'
          }
        </Typography>
        
        {/* Skeleton table for better UX */}
        <Box sx={{ mt: 3, width: '100%', maxWidth: 800 }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.200', height: 40 }}>Loading...</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.200', height: 40 }}>Loading...</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.200', height: 40 }}>Loading...</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.200', height: 40 }}>Loading...</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3].map((row) => (
                  <TableRow key={row}>
                    <TableCell sx={{ bgcolor: 'grey.100', height: 32 }}>—</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', height: 32 }}>—</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', height: 32 }}>—</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', height: 32 }}>—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error && !hasData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          icon={<ErrorIcon />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={refreshData}
            >
              Retry
            </Button>
          }
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Failed to load financial data
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {error.message}
          </Typography>
          {error.status && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Error Code: {error.status}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 4,
        minHeight: 300,
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 2
      }}>
        <MetricsIcon sx={{ 
          fontSize: { xs: 48, sm: 64 }, 
          color: 'text.secondary', 
          mb: 2 
        }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No financial data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
          There are no campaigns with financial data matching your current filters. 
          Try adjusting the date range or other filters.
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={refreshData}
          sx={{ mt: 2 }}
        >
          Refresh Data
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', ...sx }} className={className} {...props}>
      {/* Data Status Bar */}
      {(error || lastFetched) && (
        <Box sx={{ mb: 2 }}>
          {error && hasData && (
            <Alert 
              severity="warning" 
              onClose={clearError}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                Warning: {error.message}. Showing cached data.
              </Typography>
            </Alert>
          )}
          
          {lastFetched && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
            }}>
              <Typography variant="caption" color="text.secondary">
                Last updated: {getDataFreshness()} • {(currentData?.length || 0) + (snapshotData?.length || 0)} records
                {meta?.totalCampaigns && ` • ${meta.totalCampaigns} campaigns`}
              </Typography>
              <Button 
                size="small" 
                startIcon={<RefreshIcon />}
                onClick={refreshData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          )}
        </Box>
      )}


      {/* Brand Filter Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={selectedBrandFilter}
          onChange={handleBrandFilterChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 1
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              minWidth: 'auto',
              px: 2,
              py: 1
            }
          }}
        >
          <Tab value="All" label="All" />
          {availableBrands.map((brand) => (
            <Tab key={brand} value={brand} label={brand} />
          ))}
        </Tabs>
      </Box>

      {/* Hierarchical Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '& .MuiTable-root': {
            minWidth: 'max-content', // Allow table to grow based on content
            tableLayout: 'auto' // Enable auto column sizing
          }
        }}
      >
        <Table 
          size="small" 
          stickyHeader 
          sx={{ 
            borderCollapse: 'separate', 
            borderSpacing: 0,
            tableLayout: 'fixed', // Force consistent column widths
            width: '100%'
          }}
        >
          <TableHead>
            {/* Main Header Row */}
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              {/* Brand/Campaign Column - spans 2 rows */}
              <TableCell 
                rowSpan={2}
                sx={{ 
                  fontWeight: 700, 
                  width: 200,
                  minWidth: 200,
                  maxWidth: 200, 
                  bgcolor: colorScheme.neutral.bg, 
                  border: `1px solid ${colorScheme.neutral.border}`,
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  color: colorScheme.neutral.text,
                }}
              >
                Brand / Campaign
              </TableCell>
              
              {/* Last Month - spans 2 columns */}
              <TableCell 
                colSpan={2}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.givenSnapshot.bg, 
                  border: `1px solid ${colorScheme.givenSnapshot.border}`,
                  textAlign: 'center',
                  color: colorScheme.givenSnapshot.text
                }}
              >
                {isSnapshotMode ? `${lastMonth} (n-1 snapshot)` : `${lastMonth} (snapshot)`}
              </TableCell>
              
              {/* This Month - spans 2 columns */}
              <TableCell 
                colSpan={2}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.given.bg, 
                  border: `1px solid ${colorScheme.given.border}`,
                  textAlign: 'center',
                  color: colorScheme.given.text
                }}
              >
                {isSnapshotMode ? `${thisMonth} (selected snapshot)` : `${thisMonth} (current)`}
              </TableCell>
              
              {/* COMPARISON - spans 2 columns */}
              <TableCell 
                colSpan={2}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.summary.bg, 
                  border: `1px solid ${colorScheme.summary.border}`,
                  textAlign: 'center',
                  color: colorScheme.summary.text
                }}
              >
                COMPARISON ({thisMonth} vs {lastMonth})
              </TableCell>
              
              {/* ICON - spans 4 columns */}
              <TableCell 
                colSpan={4}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.iconMain.bg, 
                  border: `1px solid ${colorScheme.iconMain.border}`,
                  textAlign: 'center',
                  color: colorScheme.iconMain.text,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  letterSpacing: '0.5px'
                }}
              >
                📱 ICON (Social Media)
              </TableCell>
              
              {/* RS COST - spans 4 columns */}
              <TableCell 
                colSpan={4}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.rsPrimary.bg, 
                  border: `1px solid ${colorScheme.rsPrimary.border}`,
                  textAlign: 'center',
                  color: colorScheme.rsPrimary.text,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  letterSpacing: '0.5px'
                }}
              >
                🎯 RS COST (Paid Media)
              </TableCell>
              
              {/* OVERVIEW - spans 4 columns */}
              <TableCell 
                colSpan={4}
                sx={{ 
                  fontWeight: 700, 
                  bgcolor: colorScheme.overviewPrimary.bg, 
                  border: `1px solid ${colorScheme.overviewPrimary.border}`,
                  textAlign: 'center',
                  color: colorScheme.overviewPrimary.text,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  letterSpacing: '0.5px'
                }}
              >
                📊 OVERVIEW (Analysis)
              </TableCell>
            </TableRow>
            
            {/* Sub Header Row */}
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              {/* Last Month sub-headers (2 columns) */}
              <ColorCodedCell value="PAID MEDIA" type="givenSnapshot" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="SOCIAL MEDIA" type="givenSocialSnapshot" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              
              {/* This Month sub-headers (2 columns) */}
              <ColorCodedCell value="PAID MEDIA" type="given" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="SOCIAL MEDIA" type="givenSocial" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              
              {/* COMPARISON sub-headers (2 columns) */}
              <ColorCodedCell value="TOTAL MEDIA + SOCIAL" type="summary" align="center" autoScale={false} sx={{ minWidth: 150, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
              <ColorCodedCell value={`GAP (${thisMonth} vs ${lastMonth})`} type="highlight" align="center" autoScale={false} sx={{ minWidth: 140, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              
              {/* ICON sub-headers (4 columns) */}
              <ColorCodedCell value="ICON GAP" type="iconMain" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="ICON NOTE" type="iconSecondary" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="PLANNING 2025" type="iconTertiary" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="ACTUAL COST" type="iconLight" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              
              {/* RS COST sub-headers (4 columns) */}
              <ColorCodedCell value="RS GAP" type="rsPrimary" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="RS NOTE" type="rsSecondary" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="RS PLANNING" type="rsTertiary" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="RS ACTUAL" type="rsLight" align="center" autoScale={false} sx={{ minWidth: 130, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              
              {/* OVERVIEW sub-headers (4 columns) */}
              <ColorCodedCell value="UNUSED BUDGET" type="overviewPrimary" align="center" autoScale={false} sx={{ minWidth: 140, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="CLASSIFY" type="overviewSecondary" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="TOTAL SAVING" type="overviewTertiary" align="center" autoScale={false} sx={{ minWidth: 140, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
              <ColorCodedCell value="SAVING %" type="overviewLight" align="center" autoScale={false} sx={{ minWidth: 120, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((brand, brandIndex) => (
              <React.Fragment key={`brand-${brandIndex}`}>
                {/* Brand Row */}
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                  <TableCell sx={{ 
                    bgcolor: colorScheme.neutral.bg, 
                    border: `1px solid ${colorScheme.neutral.border}`,
                    width: 200,
                    minWidth: 200,
                    maxWidth: 200,
                    py: 1.5,
                    px: 2,
                    verticalAlign: 'top'
                  }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => toggleBrandExpansion(brand.brand_name)}
                      >
                        {expandedBrands.has(brand.brand_name) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 700, 
                          color: colorScheme.neutral.text,
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: 1.2,
                          maxWidth: 160,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' }
                        }}
                        title={brand.brand_name}
                      >
                        {brand.brand_name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  
                  {/* {lastMonth} (snapshot data) - 2 columns */}
                  <ColorCodedCell value={getTotals(brand.totals.rsCost, 'snapshot').given} type="givenSnapshot" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                  <ColorCodedCell value={getTotals(brand.totals.icon, 'snapshot').given_social} type="givenSocialSnapshot" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                  
                  {/* {thisMonth} (current data) - 2 columns */}
                  <ColorCodedCell value={getTotals(brand.totals.rsCost, 'current').given} type="given" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                  <ColorCodedCell value={getTotals(brand.totals.icon, 'current').given_social} type="givenSocial" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                  
                  {/* COMPARISON - 2 columns */}
                  <ColorCodedCell value={getTotals(brand.totals.rsCost, 'current').given + getTotals(brand.totals.icon, 'current').given_social} type="summary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                  <ColorCodedCell 
                    value={calculateGap(
                      (getTotals(brand.totals.rsCost, 'current').given || 0) + (getTotals(brand.totals.icon, 'current').given_social || 0),
                      (getTotals(brand.totals.rsCost, 'snapshot').given || 0) + (getTotals(brand.totals.icon, 'snapshot').given_social || 0)
                    )} 
                    type="highlight" 
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 600 }}
                  />
                  
                  {/* ICON - 4 columns (Social media data) */}
                  <ColorCodedCell 
                    value={calculateGap(
                      getTotals(brand.totals.icon, 'current').given_social || 0,
                      getTotals(brand.totals.icon, 'snapshot').given_social || 0
                    )} 
                    type="iconMain" 
                  />
                  <TableCell sx={{ bgcolor: colorScheme.iconSecondary.bg, border: `1px solid ${colorScheme.iconSecondary.border}`, textAlign: 'center', fontWeight: 600, color: colorScheme.iconSecondary.text }}>—</TableCell>
                  <ColorCodedCell value={getTotals(brand.totals.icon, 'current').planning} type="iconTertiary" />
                  <ColorCodedCell value={getTotals(brand.totals.icon, 'current').actual} type="iconLight" />
                  
                  {/* RS COST - 4 columns (Non-social media data) */}
                  <ColorCodedCell 
                    value={calculateGap(
                      getTotals(brand.totals.rsCost, 'current').given || 0,
                      getTotals(brand.totals.rsCost, 'snapshot').given || 0
                    )} 
                    type="rsPrimary" 
                  />
                  <TableCell sx={{ bgcolor: colorScheme.rsSecondary.bg, border: `1px solid ${colorScheme.rsSecondary.border}`, textAlign: 'center', fontWeight: 600, color: colorScheme.rsSecondary.text }}>—</TableCell>
                  <ColorCodedCell value={getTotals(brand.totals.rsCost, 'current').planning} type="rsTertiary" />
                  <ColorCodedCell value={getTotals(brand.totals.rsCost, 'current').actual} type="rsLight" />
                  
                  {/* OVERVIEW - 4 columns */}
                  {/* UNUSED BUDGET = (given + given_social) - planning */}
                  <ColorCodedCell value={
                    (getTotals(brand.totals.combined, 'current').given + getTotals(brand.totals.combined, 'current').given_social) - 
                    getTotals(brand.totals.combined, 'current').planning
                  } type="overviewPrimary" />
                  <TableCell sx={{ bgcolor: colorScheme.overviewSecondary.bg, textAlign: 'center', fontWeight: 600, color: colorScheme.overviewSecondary.text }}>—</TableCell>
                  <ColorCodedCell value={getTotals(brand.totals.combined, 'current').savings} type="overviewTertiary" />
                  <ColorCodedCell value={formatPercentage(calculateSavingsPercentage(getTotals(brand.totals.combined, 'current').planning, getTotals(brand.totals.combined, 'current').actual))} type="overviewLight" />
                </TableRow>

                {/* Brand Campaigns */}
                {expandedBrands.has(brand.brand_name) && 
                  brand.campaigns.map((campaign, campaignIndex) => (
                    <React.Fragment key={`campaign-${brandIndex}-${campaignIndex}`}>
                      {/* Campaign Row */}
                      <motion.tr
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ backgroundColor: alpha(theme.palette.primary.main, 0.01) }}
                      >
                        <TableCell sx={{ 
                          pl: 6,
                          pr: 2,
                          py: 1.5, 
                          bgcolor: colorScheme.neutral.bg, 
                          border: `1px solid ${colorScheme.neutral.border}`,
                          width: 200,
                          minWidth: 200,
                          maxWidth: 200,
                          verticalAlign: 'top'
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: colorScheme.neutral.text,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: 1.2,
                              maxWidth: 180,
                              fontSize: { xs: '0.7rem', sm: '0.8rem' }
                            }}
                            title={campaign.campaign_name}
                          >
                            {campaign.campaign_name}
                          </Typography>
                        </TableCell>
                                
                                {/* {lastMonth} (snapshot data) - 2 columns */}
                                <ColorCodedCell value={getTotals(campaign.totals.rsCost, 'snapshot').given} type="givenSnapshot" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                                <ColorCodedCell value={getTotals(campaign.totals.icon, 'snapshot').given_social} type="givenSocialSnapshot" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                                
                                {/* {thisMonth} (current data) - 2 columns */}
                                <ColorCodedCell value={getTotals(campaign.totals.rsCost, 'current').given} type="given" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                                <ColorCodedCell value={getTotals(campaign.totals.icon, 'current').given_social} type="givenSocial" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                                
                                {/* COMPARISON - 2 columns */}
                                <ColorCodedCell value={getTotals(campaign.totals.rsCost, 'current').given + getTotals(campaign.totals.icon, 'current').given_social} type="summary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }} />
                                <ColorCodedCell 
                                  value={calculateGap(
                                    (getTotals(campaign.totals.rsCost, 'current').given || 0) + (getTotals(campaign.totals.icon, 'current').given_social || 0),
                                    (getTotals(campaign.totals.rsCost, 'snapshot').given || 0) + (getTotals(campaign.totals.icon, 'snapshot').given_social || 0)
                                  )} 
                                  type="highlight" 
                                  sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 600 }}
                                />
                                
                                {/* ICON - 4 columns (Social media data) */}
                                <ColorCodedCell 
                                  value={calculateGap(
                                    getTotals(campaign.totals.icon, 'current').given_social || 0,
                                    getTotals(campaign.totals.icon, 'snapshot').given_social || 0
                                  )} 
                                  type="iconMain" 
                                />
                                <TableCell sx={{ bgcolor: colorScheme.iconSecondary.bg, border: `1px solid ${colorScheme.iconSecondary.border}`, textAlign: 'center', fontWeight: 500, color: colorScheme.iconSecondary.text }}>—</TableCell>
                                <ColorCodedCell value={getTotals(campaign.totals.icon, 'current').planning} type="iconTertiary" />
                                <ColorCodedCell value={getTotals(campaign.totals.icon, 'current').actual} type="iconLight" />
                                
                                {/* RS COST - 4 columns (Non-social media data) */}
                                <ColorCodedCell 
                                  value={calculateGap(
                                    getTotals(campaign.totals.rsCost, 'current').given || 0,
                                    getTotals(campaign.totals.rsCost, 'snapshot').given || 0
                                  )} 
                                  type="rsPrimary" 
                                />
                                <TableCell sx={{ bgcolor: colorScheme.rsSecondary.bg, border: `1px solid ${colorScheme.rsSecondary.border}`, textAlign: 'center', fontWeight: 500, color: colorScheme.rsSecondary.text }}>—</TableCell>
                                <ColorCodedCell value={getTotals(campaign.totals.rsCost, 'current').planning} type="rsTertiary" />
                                <ColorCodedCell value={getTotals(campaign.totals.rsCost, 'current').actual} type="rsLight" />
                                
                                {/* OVERVIEW - 4 columns */}
                                {/* UNUSED BUDGET = (given + given_social) - planning */}
                                <ColorCodedCell value={(() => {
                                  const givenTotal = getTotals(campaign.totals.combined, 'current').given;
                                  const givenSocialTotal = getTotals(campaign.totals.combined, 'current').given_social;
                                  const totalGiven = givenTotal + givenSocialTotal;
                                  const planningTotal = getTotals(campaign.totals.combined, 'current').planning;
                                  const unused = totalGiven - planningTotal;
                                  
                                  // Remove debug logging to prevent infinite loop
                                  
                                  return unused;
                                })()} type="overviewPrimary" />
                                <TableCell sx={{ bgcolor: colorScheme.overviewSecondary.bg, border: `1px solid ${colorScheme.overviewSecondary.border}`, textAlign: 'center', fontWeight: 500, color: colorScheme.overviewSecondary.text }}>—</TableCell>
                        <ColorCodedCell value={getTotals(campaign.totals.combined, 'current').savings} type="overviewTertiary" />
                        <ColorCodedCell value={formatPercentage(calculateSavingsPercentage(getTotals(campaign.totals.combined, 'current').planning, getTotals(campaign.totals.combined, 'current').actual))} type="overviewLight" />
                      </motion.tr>

                    </React.Fragment>
                  ))
                }
              </React.Fragment>
            ))}
            
            {/* Grand Total Row */}
            <TableRow sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderTop: `1px solid ${theme.palette.primary.main}`,
              '& .MuiTableCell-root': {
                borderTop: `1px solid ${theme.palette.primary.main}`,
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              }
            }}>
              <TableCell sx={{ 
                bgcolor: alpha(colorScheme.neutral.bg, 0.9), 
                border: `1px solid ${colorScheme.neutral.border}`,
                fontWeight: 800,
                width: 200,
                minWidth: 200,
                maxWidth: 200
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: colorScheme.neutral.text }}>
                  GRAND TOTAL
                </Typography>
              </TableCell>
              
              {/* {lastMonth} (snapshot data) - 2 columns */}
              <ColorCodedCell 
                value={grandTotals.rsCost.snapshot.given} 
                type="givenSnapshot" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              <ColorCodedCell 
                value={grandTotals.icon.snapshot.given_social} 
                type="givenSocialSnapshot" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              
              {/* {thisMonth} (current data) - 2 columns */}
              <ColorCodedCell 
                value={grandTotals.rsCost.current.given} 
                type="given" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              <ColorCodedCell 
                value={grandTotals.icon.current.given_social} 
                type="givenSocial" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              
              {/* COMPARISON - 2 columns */}
              <ColorCodedCell 
                value={grandTotals.rsCost.current.given + grandTotals.icon.current.given_social} 
                type="summary" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              <ColorCodedCell 
                value={calculateGap(
                  (grandTotals.rsCost.current.given || 0) + (grandTotals.icon.current.given_social || 0),
                  (grandTotals.rsCost.snapshot.given || 0) + (grandTotals.icon.snapshot.given_social || 0)
                )} 
                type="highlight" 
                sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' } }} 
              />
              
              {/* ICON - 4 columns (Social media data) */}
              <ColorCodedCell 
                value={calculateGap(
                  grandTotals.icon.current.given_social || 0,
                  grandTotals.icon.snapshot.given_social || 0
                )} 
                type="iconMain" 
                sx={{ fontWeight: 800 }} 
              />
              <TableCell sx={{ 
                bgcolor: colorScheme.iconSecondary.bg, 
                border: `1px solid ${colorScheme.iconSecondary.border}`,
                textAlign: 'center', 
                fontWeight: 800,
                color: colorScheme.iconSecondary.text 
              }}>—</TableCell>
              <ColorCodedCell 
                value={grandTotals.icon.current.planning} 
                type="iconTertiary" 
                sx={{ fontWeight: 800 }} 
              />
              <ColorCodedCell 
                value={grandTotals.icon.current.actual} 
                type="iconLight" 
                sx={{ fontWeight: 800 }} 
              />
              
              {/* RS COST - 4 columns (Non-social media data) */}
              <ColorCodedCell 
                value={calculateGap(
                  grandTotals.rsCost.current.given || 0,
                  grandTotals.rsCost.snapshot.given || 0
                )} 
                type="rsPrimary" 
                sx={{ fontWeight: 800 }} 
              />
              <TableCell sx={{ 
                bgcolor: colorScheme.rsSecondary.bg, 
                border: `1px solid ${colorScheme.rsSecondary.border}`,
                textAlign: 'center', 
                fontWeight: 800,
                color: colorScheme.rsSecondary.text 
              }}>—</TableCell>
              <ColorCodedCell 
                value={grandTotals.rsCost.current.planning} 
                type="rsTertiary" 
                sx={{ fontWeight: 800 }} 
              />
              <ColorCodedCell 
                value={grandTotals.rsCost.current.actual} 
                type="rsLight" 
                sx={{ fontWeight: 800 }} 
              />
              
              {/* OVERVIEW - 4 columns */}
              {/* UNUSED BUDGET = (given + given_social) - planning */}
              <ColorCodedCell 
                value={(() => {
                  const givenTotal = grandTotals.combined.current.given;
                  const givenSocialTotal = grandTotals.combined.current.given_social;
                  const totalGiven = givenTotal + givenSocialTotal;
                  const planningTotal = grandTotals.combined.current.planning;
                  const unused = totalGiven - planningTotal;
                  
                  // Remove debug logging to prevent infinite loop
                  
                  return unused;
                })()} 
                type="overviewPrimary" 
                sx={{ fontWeight: 800 }} 
              />
              <TableCell sx={{ 
                bgcolor: colorScheme.overviewSecondary.bg, 
                border: `1px solid ${colorScheme.overviewSecondary.border}`,
                textAlign: 'center', 
                fontWeight: 800,
                color: colorScheme.overviewSecondary.text 
              }}>—</TableCell>
              <ColorCodedCell 
                value={grandTotals.combined.current.savings} 
                type="overviewTertiary" 
                sx={{ fontWeight: 800 }} 
              />
              <ColorCodedCell 
                value={formatPercentage(calculateSavingsPercentage(grandTotals.combined.current.planning, grandTotals.combined.current.actual))} 
                type="overviewLight" 
                sx={{ fontWeight: 800 }} 
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HierarchicalFinancialTable; 